/**
 * LLM 配置管理器
 * 使用本地存储持久化玩家自定义的 LLM 配置
 */

import { createDefaultLLMConfig, mergeLLMConfig, type LLMConfig } from '@/IsekaiRPG/modules/llm/llmTypes';
import { LogService } from '@/IsekaiRPG/shared/logging/logService';
import { StorageService } from '@/IsekaiRPG/shared/persistence/storageService';
import { klona } from 'klona';
import { ZodError } from 'zod';

// ============================================
// 常量
// ============================================

const LLM_CONFIG_KEY = 'isekairpg_llm_config';
const CURRENT_VERSION = '1.0.0';

// ============================================
// 配置管理器
// ============================================

export class LLMSettingsManager {
  private static instance: LLMSettingsManager;
  private cachedConfig: LLMConfig | null = null;

  private constructor() {}

  /**
   * 获取单例实例
   */
  static getInstance(): LLMSettingsManager {
    if (!this.instance) {
      this.instance = new LLMSettingsManager();
    }
    return this.instance;
  }

  // ============================================
  // 配置操作
  // ============================================

  /**
   * 加载配置
   */
  async load(): Promise<LLMConfig> {
    try {
      const raw = await StorageService.getItem<unknown>(LLM_CONFIG_KEY, undefined, 'settings');

      if (!raw) {
        const defaults = this.getDefaultConfig();
        this.cachedConfig = defaults;
        LogService.info('LLMSettings', '配置为空，已加载默认配置');
        return defaults;
      }

      let merged: LLMConfig;
      try {
        merged = mergeLLMConfig(raw as Partial<LLMConfig>);
      } catch (error) {
        if (error instanceof ZodError) {
          const defaults = this.getDefaultConfig();
          this.cachedConfig = defaults;
          await this.save(defaults);
          LogService.warn('LLMSettings', '检测到无效的 LLM 配置，已重置为默认配置', error.flatten());
          return defaults;
        }
        throw error;
      }

      if (merged.version !== CURRENT_VERSION) {
        merged.version = CURRENT_VERSION;
        await this.save(merged);
      }

      this.cachedConfig = merged;
      LogService.info('LLMSettings', '已加载 LLM 配置');
      return merged;
    } catch (error) {
      LogService.error('LLMSettings', '加载 LLM 配置失败', error as Error);
      const defaults = this.getDefaultConfig();
      this.cachedConfig = defaults;
      return defaults;
    }
  }

  /**
   * 保存配置
   */
  async save(config: LLMConfig): Promise<boolean> {
    try {
      const normalized = klona(config);
      normalized.version = CURRENT_VERSION;

      const saved = await StorageService.setItem(LLM_CONFIG_KEY, normalized, CURRENT_VERSION, 'settings');
      if (!saved) {
        LogService.error('LLMSettings', '保存 LLM 配置失败：底层存储写入未成功');
        return false;
      }
      this.cachedConfig = normalized;
      LogService.info('LLMSettings', '已保存 LLM 配置');
      return true;
    } catch (error) {
      LogService.error('LLMSettings', '保存 LLM 配置失败', error as Error);
      return false;
    }
  }

  /**
   * 更新部分配置
   */
  async update(partial: Partial<LLMConfig>): Promise<LLMConfig> {
    const current = this.cachedConfig || (await this.load());
    const updated = mergeLLMConfig(
      {
        ...current,
        ...partial,
      },
      current,
    );
    await this.save(updated);
    return updated;
  }

  /**
   * 获取缓存配置
   */
  getCached(): LLMConfig | null {
    return this.cachedConfig ? klona(this.cachedConfig) : null;
  }

  // ============================================
  // 便捷方法
  // ============================================

  /**
   * 获取默认配置
   */
  private getDefaultConfig(): LLMConfig {
    const defaults = createDefaultLLMConfig();
    defaults.version = CURRENT_VERSION;
    return defaults;
  }
}

// 导出单例
export const llmSettingsManager = LLMSettingsManager.getInstance();
